import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Users, Settings, FileText, Loader2, Crown, BarChart3, MapPin, Share2 } from 'lucide-react';

import GeneralTab from '../components/admin/GeneralTab';
import UsersTab from '../components/admin/UsersTab';
import ExportTab from '../components/admin/ExportTab';
import CategoriesTab from '../components/admin/CategoriesTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import SharingTab from '../components/admin/SharingTab';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user, darkMode, toggleDarkMode } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [exportSettings, setExportSettings] = useState({
    header_title: '617Reach Community Resources',
    footer_text: '',
    logo_url: '',
    font_size: 'medium'
  });
  const [categorySettings, setCategorySettings] = useState({
    sdoh_categories: [],
    filter_mode: 'multi'
  });
  const [neighborhoodSettings, setNeighborhoodSettings] = useState({
    neighborhoods: []
  });
  const [shareSettings, setShareSettings] = useState({ templates: {}, signature: '', defaults: {} });
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('month');
  const [customReports, setCustomReports] = useState([]);
  const [newReportName, setNewReportName] = useState('');
  
  // Test Mode state
  const [testMode, setTestMode] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Neighborhood auto-fill state
  const [autofill, setAutofill] = useState(null);

  const startAutofill = async () => {
    try {
      const res = await axios.post(`${API_URL}/admin/autofill-neighborhoods`, {}, { headers: getAuthHeaders() });
      if (!res.data.job_id) { toast.info(res.data.message || 'Nothing to update'); return; }
      setAutofill({ ...res.data, status: 'processing', processed: 0, updated: 0 });
      const interval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_URL}/admin/autofill-neighborhoods/${res.data.job_id}`, { headers: getAuthHeaders() });
          setAutofill(prev => ({ ...prev, ...statusRes.data }));
          if (statusRes.data.status === 'complete') {
            clearInterval(interval);
            toast.success(`Auto-fill complete: ${statusRes.data.updated} of ${statusRes.data.total} resources updated`);
          }
        } catch {
          clearInterval(interval);
          setAutofill(null);
          toast.error('Auto-fill status check failed');
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start auto-fill');
    }
  };

  useEffect(() => {
    const fetchTestMode = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/test-mode`, { headers: getAuthHeaders() });
        setTestMode(response.data.enabled || false);
      } catch (error) {
        console.error('Failed to fetch test mode:', error.message);
      }
    };
    fetchTestMode();
  }, [getAuthHeaders]);

  const toggleTestMode = async () => {
    const newMode = !testMode;
    try {
      await axios.put(`${API_URL}/settings/test-mode`, { enabled: newMode }, { headers: getAuthHeaders() });
      setTestMode(newMode);
      toast.info(newMode ? 'Test Mode ON - Resource Lists will not be tracked' : 'Test Mode OFF - Resource Lists tracked normally');
    } catch (e) {
      toast.error('Failed to update test mode');
    }
  };

  const resetAnalytics = async () => {
    setResetting(true);
    try {
      const response = await axios.delete(`${API_URL}/analytics/reset`, { headers: getAuthHeaders() });
      toast.success(`Analytics reset! ${response.data.deleted_count} records deleted.`);
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to reset analytics');
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, exportRes, categoryRes, neighborhoodRes, shareRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/settings/export`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/settings/categories`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/settings/neighborhoods`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/settings/share`, { headers: getAuthHeaders() })
      ]);
      setUsers(usersRes.data);
      setExportSettings(exportRes.data);
      setCategorySettings(categoryRes.data);
      setNeighborhoodSettings(neighborhoodRes.data);
      setShareSettings(shareRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async (range = timeRange) => {
    setAnalyticsLoading(true);
    try {
      const [summaryRes, reportsRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/summary?time_range=${range}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/analytics/reports`, { headers: getAuthHeaders() })
      ]);
      setAnalyticsData(summaryRes.data);
      setCustomReports(reportsRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    fetchAnalytics(range);
  };

  const createCustomReport = async () => {
    if (!newReportName.trim()) { toast.error('Please enter a report name'); return; }
    try {
      await axios.post(`${API_URL}/analytics/reports`, {
        name: newReportName, metrics: ['handouts', 'top_resources', 'categories'], time_range: timeRange
      }, { headers: getAuthHeaders() });
      toast.success('Report created');
      setNewReportName('');
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to create report');
    }
  };

  const deleteReport = async (reportId) => {
    try {
      await axios.delete(`${API_URL}/analytics/reports/${reportId}`, { headers: getAuthHeaders() });
      toast.success('Report deleted');
      setCustomReports(customReports.filter(r => r.id !== reportId));
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}/role`, { role: newRole }, { headers: getAuthHeaders() });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('User role updated');
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const deleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, { headers: getAuthHeaders() });
      setUsers(users.filter(u => u.id !== userId));
      toast.success(`${userName} has been deleted`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const saveExportSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/export`, exportSettings, { headers: getAuthHeaders() });
      toast.success('Export settings saved');
    } catch (error) {
      toast.error('Failed to save export settings');
    } finally {
      setSaving(false);
    }
  };

  const saveCategorySettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/categories`, categorySettings, { headers: getAuthHeaders() });
      toast.success('Category settings saved');
    } catch (error) {
      toast.error('Failed to save category settings');
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    const newCat = prompt('Enter new category name:');
    if (newCat && !categorySettings.sdoh_categories.includes(newCat)) {
      setCategorySettings({ ...categorySettings, sdoh_categories: [...categorySettings.sdoh_categories, newCat] });
    }
  };

  const removeCategory = (cat) => {
    if (confirm(`Remove "${cat}" category?`)) {
      setCategorySettings({ ...categorySettings, sdoh_categories: categorySettings.sdoh_categories.filter(c => c !== cat) });
    }
  };

  const saveShareSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/share`, {
        templates: shareSettings.templates, signature: shareSettings.signature
      }, { headers: getAuthHeaders() });
      const res = await axios.get(`${API_URL}/settings/share`, { headers: getAuthHeaders() });
      setShareSettings(res.data);
      toast.success('Share message saved');
    } catch (error) {
      toast.error('Failed to save share message');
    } finally {
      setSaving(false);
    }
  };

  const saveNeighborhoodSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/neighborhoods`, neighborhoodSettings, { headers: getAuthHeaders() });
      toast.success('Neighborhood settings saved');
    } catch (error) {
      toast.error('Failed to save neighborhood settings');
    } finally {
      setSaving(false);
    }
  };

  const addNeighborhood = () => {
    const newNeighborhood = prompt('Enter new neighborhood name:');
    if (newNeighborhood && !neighborhoodSettings.neighborhoods.includes(newNeighborhood)) {
      setNeighborhoodSettings({ ...neighborhoodSettings, neighborhoods: [...neighborhoodSettings.neighborhoods, newNeighborhood] });
    }
  };

  const removeNeighborhood = (neighborhood) => {
    if (confirm(`Remove "${neighborhood}" neighborhood?`)) {
      setNeighborhoodSettings({ ...neighborhoodSettings, neighborhoods: neighborhoodSettings.neighborhoods.filter(n => n !== neighborhood) });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#5C4023]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F5]/95 backdrop-blur border-b border-[#E0D6C8]">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                data-testid="back-btn"
                className="rounded-full hover:bg-[#F3EFE9]"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <span className="font-['Manrope'] text-base tracking-tight mr-1"><span className="font-extrabold text-[#5C4023]">617</span><span className="font-medium text-[#C87D3E]">Reach</span></span>
                <div>
                  <h1 className="text-lg font-bold text-[#1A1A1A] font-['Manrope']">Admin Settings</h1>
                  <p className="text-xs text-[#888888]">Manage users, settings & analytics</p>
                </div>
              </div>
            </div>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
              <Crown className="w-3 h-3 mr-1" />
              Admin
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <Tabs defaultValue="general" className="space-y-6" onValueChange={(v) => v === 'analytics' && !analyticsData && fetchAnalytics()}>
          <TabsList className="bg-[#F3EFE9] p-1 rounded-xl flex-wrap">
            <TabsTrigger value="general" data-testid="general-tab" className="rounded-lg data-[state=active]:bg-white">
              <Settings className="w-4 h-4 mr-2" />General
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab" className="rounded-lg data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="export" data-testid="export-tab" className="rounded-lg data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />Export
            </TabsTrigger>
            <TabsTrigger value="sharing" data-testid="sharing-tab" className="rounded-lg data-[state=active]:bg-white">
              <Share2 className="w-4 h-4 mr-2" />Sharing
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="categories-tab" className="rounded-lg data-[state=active]:bg-white">
              <MapPin className="w-4 h-4 mr-2" />Categories
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab" className="rounded-lg data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4 mr-2" />Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <GeneralTab darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab users={users} currentUser={user} onUpdateRole={updateUserRole} onDeleteUser={deleteUser} />
          </TabsContent>

          <TabsContent value="export">
            <ExportTab exportSettings={exportSettings} setExportSettings={setExportSettings} saving={saving} onSave={saveExportSettings} />
          </TabsContent>

          <TabsContent value="sharing">
            <SharingTab shareSettings={shareSettings} setShareSettings={setShareSettings} saving={saving} onSave={saveShareSettings} />
          </TabsContent>

          <TabsContent value="categories">
            <CategoriesTab
              categorySettings={categorySettings}
              setCategorySettings={setCategorySettings}
              neighborhoodSettings={neighborhoodSettings}
              setNeighborhoodSettings={setNeighborhoodSettings}
              saving={saving}
              onSaveCategories={saveCategorySettings}
              onSaveNeighborhoods={saveNeighborhoodSettings}
              onAddCategory={addCategory}
              onRemoveCategory={removeCategory}
              onAddNeighborhood={addNeighborhood}
              onRemoveNeighborhood={removeNeighborhood}
              autofill={autofill}
              onStartAutofill={startAutofill}
            />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab
              testMode={testMode}
              onToggleTestMode={toggleTestMode}
              analyticsData={analyticsData}
              analyticsLoading={analyticsLoading}
              timeRange={timeRange}
              onTimeRangeChange={handleTimeRangeChange}
              customReports={customReports}
              newReportName={newReportName}
              setNewReportName={setNewReportName}
              onCreateReport={createCustomReport}
              onDeleteReport={deleteReport}
              resetting={resetting}
              onResetAnalytics={resetAnalytics}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminSettingsPage;
